import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RotateCcw, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { speak, cancelSpeech } from "@/lib/tts";
import { ttsLangFor } from "@/lib/i18n";
import { fireFirstWordConfetti } from "@/lib/celebrate";

/**
 * Choice board — present 2-4 giant tiles, kid picks one. Used clinically to
 * reduce cognitive load: "apple OR banana?" "park OR library?". The selected
 * tile is spoken, all others fade out, big checkmark on the chosen one.
 */
function PickerTile({ tile, onPick, "data-testid": testId }) {
  return (
    <button
      type="button"
      onClick={() => onPick(tile)}
      className="tile-btn tile-md"
      style={{ background: tile.color || "#FFFFFF" }}
      data-testid={testId}
    >
      {tile.custom_image_b64 ? (
        <img src={tile.custom_image_b64} alt="" style={{ height: "60%", width: "auto", maxWidth: "85%" }} className="object-cover rounded-xl" />
      ) : (
        <span className="tile-emoji" aria-hidden>{tile.emoji || "🔤"}</span>
      )}
      <span className="tile-label">{tile.label}</span>
    </button>
  );
}

export default function ChoiceBoard() {
  const { profileId } = useParams();
  const [profile, setProfile] = useState(null);
  const [tiles, setTiles] = useState([]);
  const [picked, setPicked] = useState([]); // up to 4 selected tiles for the question
  const [chosen, setChosen] = useState(null);
  const [opening, setOpening] = useState(true); // true = showing the picker, false = showing the choice
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: ts }] = await Promise.all([
          api.get(`/profiles/${profileId}`),
          api.get(`/profiles/${profileId}/tiles`),
        ]);
        setProfile(p);
        setTiles(ts);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
    return () => cancelSpeech();
  }, [profileId]);

  const sortedTiles = useMemo(
    () => [...tiles].sort((a, b) => (a.label || "").localeCompare(b.label || "")),
    [tiles]
  );

  const lang = profile?.language || "en";
  const ttsLang = ttsLangFor(lang);

  const togglePick = (tile) => {
    setPicked((p) => {
      const exists = p.some((x) => x.id === tile.id);
      if (exists) return p.filter((x) => x.id !== tile.id);
      if (p.length >= 4) return p; // cap at 4
      return [...p, tile];
    });
  };

  const startQuestion = () => {
    if (picked.length < 2) return;
    setChosen(null);
    setOpening(false);
    // Speak each option once with a small gap, like a teacher would.
    const phrases = picked.map((t) => t.label);
    const sentence = phrases.join(", ").replace(/, ([^,]*)$/, " or $1") + "?";
    speak(sentence, {
      rate: profile?.voice_rate ?? 0.95,
      pitch: profile?.voice_pitch ?? 1.05,
      voiceName: profile?.voice_name,
      lang: ttsLang,
    });
  };

  const onPickFinal = (tile) => {
    if (chosen) return;
    setChosen(tile);
    cancelSpeech();
    speak(tile.speak_text || tile.label, {
      rate: profile?.voice_rate ?? 0.95,
      pitch: profile?.voice_pitch ?? 1.05,
      voiceName: profile?.voice_name,
      lang: ttsLang,
    });
    setTimeout(() => fireFirstWordConfetti(), 300);
  };

  const reset = () => {
    setChosen(null);
    setPicked([]);
    setOpening(true);
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="choice-loading">
        <div className="display text-2xl">{error || "Loading…"}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-8 py-6">
      <div className="max-w-5xl mx-auto">
        <header className="flex items-center gap-3 mb-5">
          <Link to={`/board/${profileId}`} className="pill-btn" data-testid="choice-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold">Choice board</h1>
          <div className="flex-1" />
          <button type="button" className="pill-btn" onClick={reset} data-testid="choice-reset">
            <RotateCcw size={16} />
            <span className="hidden sm:inline">New question</span>
          </button>
        </header>

        {opening ? (
          <>
            <div className="brutal-card bg-white p-4 mb-4">
              <div className="display font-semibold mb-1">Pick 2 – 4 tiles for the question</div>
              <div className="text-[color:var(--ink-soft)] text-sm">
                Pick options like <em>apple</em> and <em>banana</em>, or <em>park</em> and <em>library</em>.
                Tap up to four. We'll show them as giant cards and read them aloud.
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="choice-picked">
                {picked.length === 0 && (
                  <span className="text-[color:var(--ink-soft)] text-sm italic">Nothing picked yet.</span>
                )}
                {picked.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => togglePick(t)}
                    className="pill-btn gold"
                  >
                    <span style={{ fontSize: 18 }} aria-hidden>{t.emoji || "🔤"}</span>
                    <span>{t.label}</span>
                    <X size={12} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={startQuestion}
                disabled={picked.length < 2}
                className="pill-btn primary mt-3"
                data-testid="choice-start"
              >
                Ask the question
              </button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="choice-tile-grid">
              {sortedTiles.map((t) => {
                const isOn = picked.some((x) => x.id === t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => togglePick(t)}
                    className={`tile-btn tile-md ${isOn ? "ring-4 ring-[color:var(--gold)]" : ""}`}
                    style={{ background: t.color || "#FFFFFF" }}
                    data-testid={`choice-pick-${t.id}`}
                  >
                    {t.custom_image_b64 ? (
                      <img src={t.custom_image_b64} alt="" style={{ height: "60%", width: "auto", maxWidth: "85%" }} className="object-cover rounded-xl" />
                    ) : (
                      <span className="tile-emoji" aria-hidden>{t.emoji || "🔤"}</span>
                    )}
                    <span className="tile-label">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div
            className={`grid gap-4 sm:gap-6 items-stretch ${
              picked.length === 2 ? "grid-cols-1 sm:grid-cols-2" :
              picked.length === 3 ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-2 sm:grid-cols-4"
            }`}
            data-testid="choice-question"
          >
            {picked.map((t) => {
              const dimmed = chosen && chosen.id !== t.id;
              const winner = chosen && chosen.id === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onPickFinal(t)}
                  className={`brutal-card p-6 flex flex-col items-center justify-center min-h-[280px] sm:min-h-[360px] transition-all ${
                    dimmed ? "opacity-30 scale-95" : ""
                  } ${winner ? "ring-8 ring-[color:var(--gold)]" : ""}`}
                  style={{ background: t.color || "#FBEFD9" }}
                  data-testid={`choice-final-${t.id}`}
                >
                  {t.custom_image_b64 ? (
                    <img src={t.custom_image_b64} alt="" className="object-cover rounded-2xl" style={{ height: 140 }} />
                  ) : (
                    <div className="text-7xl mb-3" aria-hidden>{t.emoji || "🔤"}</div>
                  )}
                  <div className="display text-2xl sm:text-3xl font-extrabold text-center">{t.label}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
